import PropTypes from 'prop-types';

import React, {
    createContext,
    useContext,
    useMemo,
    useState,
    useEffect
} from 'react';
import { use } from 'react';

const noop = async () => { };

/* ========================================================================= */
/* Hook(s) */
/* ========================================================================= */

const GlobalLayoutContext = createContext({
    /** @type {'INTRO' | 'TRANSPARENT' | 'PEEKABOO'} */
    navigationPhase: 'PEEKABOO',
    /** @type {(param: 'INTRO' | 'TRANSPARENT' | 'PEEKABOO') => void} */
    setNavigationPhase: () => { },
    /** whether the header is visible when scrolling up */
    isHeaderVisibleOnScrollUp: true,
    /** setter for isHeaderVisibleOnScrollUp */
    setIsHeaderVisibleOnScrollUp: () => { }
});

/* ========================================================================= */
/* Hook(s) */
/* ========================================================================= */

/**
 * @hook useGlobalLayout
 */
const useGlobalLayout = () => {
    const context = useContext(GlobalLayoutContext);
    if (context === undefined) {
        throw new Error('useGlobalLayout must be used within a GlobalLayoutProvider.');
    }
    return context;
};

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @component GlobalLayoutProvider
 * @props (see propTypes)
 */
const GlobalLayoutProvider = ({ children, initialNavigationPhase = 'LANDINGHERO' }) => {
    const [navigationPhase, setNavigationPhase] = useState(initialNavigationPhase);
    const [headerOpen, setHeaderOpen] = useState(false);
    const [isHeaderVisibleOnScrollUp, setIsHeaderVisibleOnScrollUp] = useState(true);
    // render
    return (
        <GlobalLayoutContext.Provider value={useMemo(() => {
            return {
                headerOpen,
                setHeaderOpen,
                isHeaderVisibleOnScrollUp,
                setIsHeaderVisibleOnScrollUp,
                initialNavigationPhase,
                navigationPhase,
                setNavigationPhase,
            };
        }, [
            headerOpen,
            setHeaderOpen,
            isHeaderVisibleOnScrollUp,
            setIsHeaderVisibleOnScrollUp,
            navigationPhase,
            setNavigationPhase,
        ])}>
            {children}
        </GlobalLayoutContext.Provider>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

GlobalLayoutContext.displayName = 'GlobalLayoutContext';

GlobalLayoutProvider.propTypes = {
    children: PropTypes.any,
    initialNavigationPhase: PropTypes.string,
};

export {
    GlobalLayoutContext,
    GlobalLayoutProvider,
    useGlobalLayout
};
